import type { CastProfile } from './castProfiles.ts'
import type { CastProfileTimeline } from './castProfileTimelines.ts'

// Individually researched original editorial copy, reviewed October 1, 2026.
// Future season 8 cards are announced, never aired recaps.
// Historical relationship events do not certify current status or episode appearances.
export const legacyProfilesC: CastProfile[] = [
  {
    "personId": "p-aladin-jallali",
    "headline": "An online romance moved to Qatar, then a wedding celebration moved the conversation to Tunisia.",
    "bio": [
      "Aladin was introduced as a personal trainer working in Qatar whose Facebook connection with Laura quickly became an engagement. Her move brought their long-distance hopes into everyday life, where adjusting to one another required more than a good Wi-Fi signal.",
      "Their Tunisian wedding celebration could not settle the disagreements that followed. Laura later reported their divorce, while Aladin announced another engagement in 2020; those are dated developments, not a freshly checked relationship update."
    ],
    "originStory": "Eight months of Facebook conversation led to Laura’s visit and a swift engagement. The Other Way followed her move to Qatar to build a life with Aladin.",
    "journeys": [
      {
        "label": "Where it started: Aladin & Laura",
        "partnerName": "Laura Jallali",
        "relationshipId": "ARC-8498E016A0E7",
        "era": "The Other Way S1",
        "summary": "A fast engagement became marriage and a Tunisian celebration, followed by a split; Laura reported their divorce finalized in 2020."
      },
      {
        "label": "A later engagement",
        "partnerName": "Maria Talebi",
        "era": "After their televised relationship",
        "summary": "2020 reporting identified Aladin’s later fiancée as Maria. No current relationship outcome is certified here."
      }
    ],
    "sources": [
      {
        "label": "TLC: The Other Way’s original couples",
        "url": "https://www.tlc.com/shows/90-day-fiance/articles/90-day-fiance--the-other-way-"
      },
      {
        "label": "Monsters & Critics: Laura’s divorce interview",
        "url": "https://www.monstersandcritics.com/tv/reality-tv/aladin-and-laura-jallali-are-finally-divorced-heres-what-she-got-in-the-settlement/"
      },
      {
        "label": "Monsters & Critics: Laura’s later online connection",
        "url": "https://www.monstersandcritics.com/tv/reality-tv/laura-jallali-gives-update-on-new-boyfriend-tony-90-day-fiance-alum-reveals-whether-theyve-met-or-not/"
      },
      {
        "label": "Monsters & Critics: Aladin’s 2020 engagement announcement",
        "url": "https://www.monstersandcritics.com/tv/reality-tv/laura-jallali-rages-at-aladin-for-getting-engaged-while-they-are-still-married-demands-her-stuff-back-again/"
      }
    ]
  },
  {
    "personId": "p-laura-jallali",
    "headline": "A retirement reset, an international wedding and a family meeting nobody could put on mute.",
    "bio": [
      "Laura was living in Florida when months of Facebook messages led her to visit Aladin in Qatar. An engagement followed almost immediately, and The Other Way watched her trade familiar routines for a marriage abroad. This was not the usual retirement brochure.",
      "Her son Liam’s reservations and disagreements with Aladin became major parts of the story. Laura reported their divorce finalized in 2020, then discussed an online connection with Tony that had not yet become an in-person meeting."
    ],
    "originStory": "Laura joined The Other Way with Aladin after their online courtship and engagement, preparing to relocate to Qatar and celebrate their wedding in Tunisia.",
    "journeys": [
      {
        "label": "Where it started: Laura & Aladin",
        "partnerName": "Aladin Jallali",
        "relationshipId": "ARC-8498E016A0E7",
        "era": "The Other Way S1",
        "summary": "The relationship began online, became marriage and ended; Laura announced a finalized divorce in October 2020."
      },
      {
        "label": "Later online dating",
        "partnerName": "Tony",
        "era": "2020 update",
        "summary": "Laura described a friendship that became an online romance. At that interview they had not met in person."
      }
    ],
    "sources": [
      {
        "label": "TLC: The Other Way’s original couples",
        "url": "https://www.tlc.com/shows/90-day-fiance/articles/90-day-fiance--the-other-way-"
      },
      {
        "label": "Monsters & Critics: Laura’s divorce interview",
        "url": "https://www.monstersandcritics.com/tv/reality-tv/aladin-and-laura-jallali-are-finally-divorced-heres-what-she-got-in-the-settlement/"
      },
      {
        "label": "Monsters & Critics: Laura’s later online connection",
        "url": "https://www.monstersandcritics.com/tv/reality-tv/laura-jallali-gives-update-on-new-boyfriend-tony-90-day-fiance-alum-reveals-whether-theyve-met-or-not/"
      }
    ]
  },
  {
    "personId": "p-deavan-clegg",
    "headline": "Utah to South Korea, with two children and considerably more than two opinions about the plan.",
    "bio": [
      "Deavan’s dating-app match with Jihoon became a real-life visit, a pregnancy and a plan to raise a family in South Korea. Viewers met a Utah mother trying to combine romance, parenting and international relocation—three jobs with very little shared instruction manual.",
      "Their two Other Way seasons followed money worries, family friction and attempts to make a home together. After the split she returned to the United States and publicly began a later relationship with Christopher “Topher” Park."
    ],
    "originStory": "After meeting Jihoon through a dating app and welcoming their son, Deavan entered The Other Way to pursue marriage and family life in South Korea.",
    "journeys": [
      {
        "label": "Where it started: Deavan & Jihoon",
        "partnerName": "Jihoon Lee",
        "relationshipId": "ARC-6EC78319E282",
        "era": "The Other Way S1–S2",
        "summary": "Their online match became marriage and parenthood before a 2020 separation. Deavan later announced their divorce finalized in May 2022."
      },
      {
        "label": "A later relationship",
        "partnerName": "Christopher “Topher” Park",
        "era": "After the separation",
        "summary": "Deavan publicly introduced her relationship with Topher after returning to the United States; ET reported their 2021 public appearance together."
      }
    ],
    "sources": [
      {
        "label": "TLC: The Other Way’s original couples",
        "url": "https://www.tlc.com/shows/90-day-fiance/articles/90-day-fiance--the-other-way-"
      },
      {
        "label": "TLC: The Other Way season 2 couples",
        "url": "https://www.tlc.com/shows/90-day-fiance/photo-galleries/meet-the-couples-of-90-day-fiance--the-other-way-season-2"
      },
      {
        "label": "TLC: Deavan and Jihoon welcome Taeyang",
        "url": "https://www.tlc.com/shows/90-day-fiance/articles/a-90-day-fiance-baby-for-deavan-and-jihoon"
      },
      {
        "label": "ET: Deavan’s later relationship with Topher",
        "url": "https://www.etonline.com/90-day-fiance-star-deavan-makes-public-appearance-with-boyfriend-topher-after-split-from-jihoon"
      },
      {
        "label": "Us Weekly: Deavan and Jihoon’s post-season split",
        "url": "https://www.usmagazine.com/celebrity-news/news/90-day-fiance-deavan-cleggs-boyfriend-has-stepped-in-with-kids/"
      },
      {
        "label": "Reality TV World: Deavan’s divorce announcement",
        "url": "https://www.realitytvworld.com/news/90-day-fiance-alum-deavan-clegg-announces-jihoon-lee-divorce-has-been-finalized--31109.php"
      }
    ]
  },
  {
    "personId": "p-jihoon-lee",
    "headline": "A dating-app introduction became instant parenthood—and an international test of the household budget.",
    "bio": [
      "Jihoon’s trip to meet Deavan in Utah turned their online relationship into a family story. Their son Taeyang arrived while they were still working out how to live in the same country, giving the new dad a to-do list that came with its own passport requirements.",
      "When Deavan moved to South Korea, the couple struggled over finances, housing and expectations of responsibility. They separated in 2020; Deavan later reported their divorce finalized. That history should not be mistaken for a freshly verified account of Jihoon’s present dating life."
    ],
    "originStory": "Jihoon met Deavan through a dating app, visited her in Utah and became a father. Their attempt to build a married family together in South Korea introduced him on The Other Way.",
    "journeys": [
      {
        "label": "Where it started: Jihoon & Deavan",
        "partnerName": "Deavan Clegg",
        "relationshipId": "ARC-6EC78319E282",
        "era": "The Other Way S1–S2",
        "summary": "The pair became parents and married before splitting in 2020. Deavan publicly reported a finalized divorce in May 2022."
      }
    ],
    "sources": [
      {
        "label": "TLC: The Other Way’s original couples",
        "url": "https://www.tlc.com/shows/90-day-fiance/articles/90-day-fiance--the-other-way-"
      },
      {
        "label": "TLC: The Other Way season 2 couples",
        "url": "https://www.tlc.com/shows/90-day-fiance/photo-galleries/meet-the-couples-of-90-day-fiance--the-other-way-season-2"
      },
      {
        "label": "TLC: Deavan and Jihoon welcome Taeyang",
        "url": "https://www.tlc.com/shows/90-day-fiance/articles/a-90-day-fiance-baby-for-deavan-and-jihoon"
      },
      {
        "label": "Us Weekly: Deavan and Jihoon’s post-season split",
        "url": "https://www.usmagazine.com/celebrity-news/news/90-day-fiance-deavan-cleggs-boyfriend-has-stepped-in-with-kids/"
      },
      {
        "label": "Reality TV World: Deavan’s divorce announcement",
        "url": "https://www.realitytvworld.com/news/90-day-fiance-alum-deavan-clegg-announces-jihoon-lee-divorce-has-been-finalized--31109.php"
      }
    ]
  },
  {
    "personId": "p-melyza-zeta",
    "headline": "A Colombia reunion with plenty of history—and no express lane back to trust.",
    "bio": [
      "Melyza met Tim while she was working as an au pair in the United States. They continued across borders, but his admitted infidelity changed the plan from her moving to Texas to him trying to repair the relationship in Colombia. A plane ticket was a start, not a receipt for forgiveness.",
      "Their Other Way chapter put the trust problem and her family’s doubts in full view. After a breakup, the pair reunited and announced an engagement on Bares All in 2021; the proposal was a new chapter, not proof that every old problem had packed up and left."
    ],
    "originStory": "Melyza’s long-distance relationship with Tim Clarkson brought her to The Other Way Season 2 when he moved to Colombia after breaching her trust.",
    "journeys": [
      {
        "label": "Where it started: Melyza & Tim",
        "partnerName": "Tim Clarkson",
        "relationshipId": "ARC-0745DBD6E961",
        "era": "The Other Way S2 · 2021 update",
        "summary": "Their long-distance romance faced infidelity and a breakup. Both announced a later engagement on Bares All in March 2021; their current status needs separate verification."
      }
    ],
    "sources": [
      {
        "label": "TLC: The Other Way season 2 couples",
        "url": "https://www.tlc.com/shows/90-day-fiance/photo-galleries/meet-the-couples-of-90-day-fiance--the-other-way-season-2"
      },
      {
        "label": "ET: Melyza explains how she met Tim",
        "url": "https://www.etonline.com/90-day-fiance-melyza-says-she-was-not-looking-for-a-white-boy-when-she-met-tim-exclusive-149764"
      },
      {
        "label": "ET: Tim discusses the trust breach",
        "url": "https://www.etonline.com/90-day-fiance-tim-explains-how-melyza-caught-him-cheating-on-her-exclusive-149112"
      },
      {
        "label": "ET: Tim and Melyza announce their engagement",
        "url": "https://www.etonline.com/90-day-bares-all-tim-and-melyza-are-engaged-in-shocking-twist-161694"
      }
    ]
  },
  {
    "personId": "p-tim-clarkson",
    "headline": "Colombia was the destination; rebuilding trust was the actual assignment.",
    "bio": [
      "Tim Clarkson—not the franchise’s other Tim—met Melyza while she was an au pair in America. Their long-distance plan changed after he admitted cheating, and he headed to Colombia hoping that showing up in person could help repair what he had damaged.",
      "The Other Way followed that difficult reunion rather than a carefree relocation. After they split, Tim and Melyza reconnected and confirmed an engagement on Bares All in 2021. Love supplied a second chance; the baggage did not waive its checked-bag fee."
    ],
    "originStory": "Tim entered The Other Way Season 2 by moving from Texas to Colombia to rebuild his relationship with Melyza after his infidelity.",
    "journeys": [
      {
        "label": "Where it started: Tim & Melyza",
        "partnerName": "Melyza Zeta",
        "relationshipId": "ARC-0745DBD6E961",
        "era": "The Other Way S2 · 2021 update",
        "summary": "An American introduction became long-distance dating and a difficult Colombia reunion. A breakup was followed by their announced 2021 engagement, not a current-status certification."
      }
    ],
    "sources": [
      {
        "label": "TLC: The Other Way season 2 couples",
        "url": "https://www.tlc.com/shows/90-day-fiance/photo-galleries/meet-the-couples-of-90-day-fiance--the-other-way-season-2"
      },
      {
        "label": "ET: Melyza explains how she met Tim",
        "url": "https://www.etonline.com/90-day-fiance-melyza-says-she-was-not-looking-for-a-white-boy-when-she-met-tim-exclusive-149764"
      },
      {
        "label": "ET: Tim discusses the trust breach",
        "url": "https://www.etonline.com/90-day-fiance-tim-explains-how-melyza-caught-him-cheating-on-her-exclusive-149112"
      },
      {
        "label": "ET: Tim and Melyza announce their engagement",
        "url": "https://www.etonline.com/90-day-bares-all-tim-and-melyza-are-engaged-in-shocking-twist-161694"
      }
    ]
  },
  {
    "personId": "p-yazan-abo-horira",
    "headline": "An international romance where the relationship needed its own family interpreter.",
    "bio": [
      "Yazan met Brittany over video chat through his sister, then welcomed her to Jordan for their Other Way story. His family’s expectations and their different ideas about faith, marriage and independence quickly made the relationship more complicated than the distance alone.",
      "The romance ended after that season. On Bares All in 2021, Yazan confirmed an engagement to Leena, a different partner whom he was communicating with long-distance. A fresh start arrived; a reliably sourced update on its present outcome still needs its own appointment."
    ],
    "originStory": "Yazan’s sister introduced him to Brittany over video chat. Brittany’s move to Jordan and their plans for a future together brought him to The Other Way Season 2.",
    "journeys": [
      {
        "label": "Where it started: Yazan & Brittany",
        "partnerName": "Brittany Banks",
        "relationshipId": "ARC-97EBA9D0AEA9",
        "era": "The Other Way S2",
        "summary": "Their Jordan chapter exposed incompatible expectations about marriage and family, and they later split."
      },
      {
        "label": "A later engagement",
        "partnerName": "Leena",
        "era": "2021 Bares All update",
        "summary": "Yazan confirmed an engagement to Leena in April 2021. That interview does not by itself verify a later wedding or current relationship status."
      }
    ],
    "sources": [
      {
        "label": "TLC: The Other Way season 2 couples",
        "url": "https://www.tlc.com/shows/90-day-fiance/photo-galleries/meet-the-couples-of-90-day-fiance--the-other-way-season-2"
      },
      {
        "label": "ET: Yazan confirms a later engagement",
        "url": "https://www.etonline.com/90-day-bares-all-yazan-reveals-hes-engaged-to-another-american-woman-163297"
      }
    ]
  },
  {
    "personId": "p-jonathan-ally-arc",
    "headline": "Singapore, a baby on the way and a budget that needs its own pep talk.",
    "bio": [
      "TLC introduces Jonathan as a divorced Connecticut father preparing to join his pregnant girlfriend Ally in Singapore. His new start has practical hurdles: limited savings and no job arranged before the move.",
      "Their upcoming story pairs impending parenthood with a delicate conversation with Ally’s family. The nursery shopping list apparently comes with a separate list of things to explain; as of October 1, this chapter has not aired."
    ],
    "originStory": "Jonathan is announced for The Other Way Season 8 through his planned move to Singapore for Ally. TLC has not explained their first meeting.",
    "journeys": [
      {
        "label": "Where it starts: Jonathan & Ally",
        "partnerName": "Ally",
        "relationshipId": "ARC-613995429020",
        "era": "The Other Way S8 · announced",
        "summary": "TLC previews Jonathan’s move to Singapore for pregnant partner Ally, with work and family conversations still unresolved. This is an announced season premise, not an account of aired events."
      }
    ],
    "sources": [
      {
        "label": "TLC: The Other Way season 8 announced couples",
        "url": "https://www.tlc.com/shows/90-day-fiance-the-other-way/photo-galleries/90-day-fiance-the-other-way-season-8-couples-bios"
      },
      {
        "label": "TLC: The Other Way season 8 premiere announcement",
        "url": "https://www.tlc.com/shows/90-day-fiance-the-other-way/articles/90-day-fiance-before-the-90-days-first-look-trailer-video-premiere-date-announcement"
      },
      {
        "label": "TV Insider: The Other Way season 8 cast announcement",
        "url": "https://www.tvinsider.com/swooon/gallery/90-day-fiance-the-other-way-season-8-cast-new-returning-couples/"
      }
    ]
  },
  {
    "personId": "p-ally-jonathan-arc",
    "headline": "A Singapore homecoming for two—with a very important third person on the way.",
    "bio": [
      "Ally is introduced as Jonathan’s pregnant girlfriend in Singapore, awaiting his move from Connecticut. Their announced story concerns starting a household while his savings and employment plans are still unsettled.",
      "TLC also previews a difficult family conversation: her parents do not yet know about the pregnancy. A fresh start is lovely, but this one has several calendar reminders attached. No episode outcome is claimed before the October 4 premiere."
    ],
    "originStory": "Ally’s relationship with Jonathan and their planned life together in Singapore introduce her in the announced Other Way Season 8 cast. Their first-meeting story is not yet sourced.",
    "journeys": [
      {
        "label": "Where it starts: Ally & Jonathan",
        "partnerName": "Jonathan",
        "relationshipId": "ARC-613995429020",
        "era": "The Other Way S8 · announced",
        "summary": "TLC previews Ally welcoming Jonathan to Singapore while preparing for parenthood and telling her family about the pregnancy. The season is announced, with no aired resolution yet."
      }
    ],
    "sources": [
      {
        "label": "TLC: The Other Way season 8 announced couples",
        "url": "https://www.tlc.com/shows/90-day-fiance-the-other-way/photo-galleries/90-day-fiance-the-other-way-season-8-couples-bios"
      },
      {
        "label": "TLC: The Other Way season 8 premiere announcement",
        "url": "https://www.tlc.com/shows/90-day-fiance-the-other-way/articles/90-day-fiance-before-the-90-days-first-look-trailer-video-premiere-date-announcement"
      },
      {
        "label": "TV Insider: The Other Way season 8 cast announcement",
        "url": "https://www.tvinsider.com/swooon/gallery/90-day-fiance-the-other-way-season-8-cast-new-returning-couples/"
      }
    ]
  },
  {
    "personId": "p-zuri-ben-arc",
    "headline": "One more try at France, with romance in the carry-on and adaptation on the itinerary.",
    "bio": [
      "Zuri met Ben while studying abroad in the United Kingdom, then tried living with him in France. TLC’s introduction says that first relocation did not work out, leaving her unsure of her place in his world.",
      "The announced season follows another attempt to make France home. Ben’s friends and questions about their time apart add pressure; the second packing list includes a few unresolved conversations alongside the sweaters."
    ],
    "originStory": "Zuri’s study-abroad meeting with Ben led to a relationship and an earlier unsuccessful move to France. Their second attempt introduces her in The Other Way Season 8.",
    "journeys": [
      {
        "label": "Where it starts: Zuri & Ben",
        "partnerName": "Ben",
        "relationshipId": "ARC-2F1D8514A67A",
        "era": "The Other Way S8 · announced",
        "summary": "TLC previews Zuri giving life in France with Ben another chance after an earlier move fell apart. Friends’ doubts and questions about their time apart are announced tensions, not confirmed outcomes."
      }
    ],
    "sources": [
      {
        "label": "TLC: The Other Way season 8 announced couples",
        "url": "https://www.tlc.com/shows/90-day-fiance-the-other-way/photo-galleries/90-day-fiance-the-other-way-season-8-couples-bios"
      },
      {
        "label": "TLC: The Other Way season 8 premiere announcement",
        "url": "https://www.tlc.com/shows/90-day-fiance-the-other-way/articles/90-day-fiance-before-the-90-days-first-look-trailer-video-premiere-date-announcement"
      },
      {
        "label": "TV Insider: The Other Way season 8 cast announcement",
        "url": "https://www.tvinsider.com/swooon/gallery/90-day-fiance-the-other-way-season-8-cast-new-returning-couples/"
      }
    ]
  },
  {
    "personId": "p-ben-zuri-arc",
    "headline": "A French second chance that needs more than a romantic postcode.",
    "bio": [
      "Ben met Zuri during her study-abroad time in the United Kingdom. Their attempt to settle in France stumbled when she struggled to adapt and he questioned whether the move could work.",
      "TLC’s upcoming chapter gives the couple another attempt at life together. His skeptical friends and unresolved questions about the separation may complicate it; apparently, the welcoming committee has follow-up questions."
    ],
    "originStory": "Ben’s relationship with Zuri began while she studied in the United Kingdom. Their renewed attempt to live together in France is his announced Other Way Season 8 introduction.",
    "journeys": [
      {
        "label": "Where it starts: Ben & Zuri",
        "partnerName": "Zuri",
        "relationshipId": "ARC-2F1D8514A67A",
        "era": "The Other Way S8 · announced",
        "summary": "TLC previews Ben and Zuri trying again to build a shared home in France. The announced storyline includes questions about adaptation, friends’ support and their time apart; it has not yet aired."
      }
    ],
    "sources": [
      {
        "label": "TLC: The Other Way season 8 announced couples",
        "url": "https://www.tlc.com/shows/90-day-fiance-the-other-way/photo-galleries/90-day-fiance-the-other-way-season-8-couples-bios"
      },
      {
        "label": "TLC: The Other Way season 8 premiere announcement",
        "url": "https://www.tlc.com/shows/90-day-fiance-the-other-way/articles/90-day-fiance-before-the-90-days-first-look-trailer-video-premiere-date-announcement"
      },
      {
        "label": "TV Insider: The Other Way season 8 cast announcement",
        "url": "https://www.tvinsider.com/swooon/gallery/90-day-fiance-the-other-way-season-8-cast-new-returning-couples/"
      }
    ]
  },
  {
    "personId": "p-jessy-vittorio-arc",
    "headline": "A Bronx pageant queen’s Italian fresh start, with sparkle and some serious questions.",
    "bio": [
      "Jessy is introduced as a Bronx pageant queen hoping her relationship with Vittorio offers a better romantic chapter. TLC’s announced story takes her to Italy to build a future with the fashion-industry insider.",
      "The preview raises questions about intimacy, family approval and what each partner has left unsaid. The setting may be glamorous, but it cannot answer those questions for them. No outcome has aired as of this profile’s October 1 review."
    ],
    "originStory": "Jessy is announced for The Other Way Season 8 through her move to Italy for Vittorio. Opened sources do not explain where or how the pair first met.",
    "journeys": [
      {
        "label": "Where it starts: Jessy & Vittorio",
        "partnerName": "Vittorio",
        "relationshipId": "ARC-554CAC8794EF",
        "era": "The Other Way S8 · announced",
        "summary": "TLC previews Jessy’s move to Italy and the couple’s questions about commitment, intimacy and family support. This is an announced relationship chapter, not a recap of aired episodes."
      }
    ],
    "sources": [
      {
        "label": "TLC: The Other Way season 8 announced couples",
        "url": "https://www.tlc.com/shows/90-day-fiance-the-other-way/photo-galleries/90-day-fiance-the-other-way-season-8-couples-bios"
      },
      {
        "label": "TLC: The Other Way season 8 premiere announcement",
        "url": "https://www.tlc.com/shows/90-day-fiance-the-other-way/articles/90-day-fiance-before-the-90-days-first-look-trailer-video-premiere-date-announcement"
      },
      {
        "label": "TV Insider: The Other Way season 8 cast announcement",
        "url": "https://www.tvinsider.com/swooon/gallery/90-day-fiance-the-other-way-season-8-cast-new-returning-couples/"
      }
    ]
  },
  {
    "personId": "p-vittorio-jessy-arc",
    "headline": "Florence fashion meets a Bronx fresh start—and a relationship with unfinished conversations.",
    "bio": [
      "Vittorio is introduced as a fashion-industry insider in Florence whose partner Jessy plans to move from New York. Their relationship brings him into TLC’s announced eighth season of The Other Way.",
      "The preview suggests family doubts and unresolved questions about their romantic connection. Style may help with first impressions, but it is not a substitute for a clear conversation. The season’s events and outcome remain upcoming."
    ],
    "originStory": "Vittorio’s relationship with Jessy and her planned move to Italy are his announced franchise introduction. Their first meeting has not been established in the opened sources.",
    "journeys": [
      {
        "label": "Where it starts: Vittorio & Jessy",
        "partnerName": "Jessy",
        "relationshipId": "ARC-554CAC8794EF",
        "era": "The Other Way S8 · announced",
        "summary": "TLC previews Vittorio and Jessy testing whether their Italian future can overcome questions about intimacy and family approval. The chapter is announced and has not aired."
      }
    ],
    "sources": [
      {
        "label": "TLC: The Other Way season 8 announced couples",
        "url": "https://www.tlc.com/shows/90-day-fiance-the-other-way/photo-galleries/90-day-fiance-the-other-way-season-8-couples-bios"
      },
      {
        "label": "TLC: The Other Way season 8 premiere announcement",
        "url": "https://www.tlc.com/shows/90-day-fiance-the-other-way/articles/90-day-fiance-before-the-90-days-first-look-trailer-video-premiere-date-announcement"
      },
      {
        "label": "TV Insider: The Other Way season 8 cast announcement",
        "url": "https://www.tvinsider.com/swooon/gallery/90-day-fiance-the-other-way-season-8-cast-new-returning-couples/"
      }
    ]
  },
  {
    "personId": "p-michelle-felix-arc",
    "headline": "A Dallas car flipper pursuing a Ghana love story—with trust checks on the checklist.",
    "bio": [
      "Michelle is a Dallas mother who flips cars for a living. She met Felix while traveling in West Africa, and TLC’s announced introduction follows her decision to relocate to Ghana for their relationship.",
      "The preview centers on whether she can feel secure with Felix and his family. A new country is a big commitment; this particular life plan needs more than a quick test drive. The season is still upcoming as of October 1."
    ],
    "originStory": "Michelle met Felix while traveling through West Africa. Her planned move from Dallas to Ghana for their relationship introduces her in The Other Way Season 8.",
    "journeys": [
      {
        "label": "Where it starts: Michelle & Felix",
        "partnerName": "Felix",
        "relationshipId": "ARC-464FCC3AA26C",
        "era": "The Other Way S8 · announced",
        "summary": "TLC previews Michelle moving to Ghana for Felix while questioning how much she can trust the relationship and family expectations. No aired outcome or allegation of wrongdoing is established here."
      }
    ],
    "sources": [
      {
        "label": "TLC: The Other Way season 8 announced couples",
        "url": "https://www.tlc.com/shows/90-day-fiance-the-other-way/photo-galleries/90-day-fiance-the-other-way-season-8-couples-bios"
      },
      {
        "label": "TLC: The Other Way season 8 premiere announcement",
        "url": "https://www.tlc.com/shows/90-day-fiance-the-other-way/articles/90-day-fiance-before-the-90-days-first-look-trailer-video-premiere-date-announcement"
      },
      {
        "label": "TV Insider: The Other Way season 8 cast announcement",
        "url": "https://www.tvinsider.com/swooon/gallery/90-day-fiance-the-other-way-season-8-cast-new-returning-couples/"
      }
    ]
  },
  {
    "personId": "p-felix-michelle-arc",
    "headline": "A Ghana romance preparing for a Dallas arrival and a very full family conversation.",
    "bio": [
      "Felix is introduced as a preacher’s son in Ghana who met Michelle during her travels in West Africa. Her planned relocation from Dallas makes their relationship part of The Other Way’s announced season 8 cast.",
      "TLC previews questions about trust and his family’s expectations as the pair prepare for life together. The welcome may need more than airport pickup; what happens next has not aired at this profile’s review date."
    ],
    "originStory": "Felix met Michelle during her West Africa travels. Their planned shared life in Ghana is his announced introduction on The Other Way Season 8.",
    "journeys": [
      {
        "label": "Where it starts: Felix & Michelle",
        "partnerName": "Michelle",
        "relationshipId": "ARC-464FCC3AA26C",
        "era": "The Other Way S8 · announced",
        "summary": "TLC previews Felix and Michelle trying to build a life in Ghana amid questions about trust and family intentions. These are announced tensions, not verified conclusions or aired outcomes."
      }
    ],
    "sources": [
      {
        "label": "TLC: The Other Way season 8 announced couples",
        "url": "https://www.tlc.com/shows/90-day-fiance-the-other-way/photo-galleries/90-day-fiance-the-other-way-season-8-couples-bios"
      },
      {
        "label": "TLC: The Other Way season 8 premiere announcement",
        "url": "https://www.tlc.com/shows/90-day-fiance-the-other-way/articles/90-day-fiance-before-the-90-days-first-look-trailer-video-premiere-date-announcement"
      },
      {
        "label": "TV Insider: The Other Way season 8 cast announcement",
        "url": "https://www.tvinsider.com/swooon/gallery/90-day-fiance-the-other-way-season-8-cast-new-returning-couples/"
      }
    ]
  },
  {
    "personId": "p-andi-nigel-arc",
    "headline": "Nashville ambition meets an Australian reset—and a relationship that needs shared definitions.",
    "bio": [
      "Andi is introduced as a Nashville musician putting a career on hold to join Nigel in Australia. TLC contrasts Andi’s planning-minded approach with Nigel’s more nomadic, laid-back lifestyle.",
      "Their announced story also concerns agreeing on what ethical non-monogamy means to each of them and what they want from family life. A relationship agreement cannot run on two different operating manuals; the season is still upcoming."
    ],
    "originStory": "Andi’s planned move from Nashville to Australia for Nigel introduces this relationship in The Other Way Season 8. Their first-meeting details have not been sourced.",
    "journeys": [
      {
        "label": "Where it starts: Andi & Nigel",
        "partnerName": "Nigel",
        "relationshipId": "ARC-D428916C0C37",
        "era": "The Other Way S8 · announced",
        "summary": "TLC previews Andi’s Australian relocation and the pair’s differing expectations about lifestyle, non-monogamy and family plans. No aired resolution is available before the announced premiere."
      }
    ],
    "sources": [
      {
        "label": "TLC: The Other Way season 8 announced couples",
        "url": "https://www.tlc.com/shows/90-day-fiance-the-other-way/photo-galleries/90-day-fiance-the-other-way-season-8-couples-bios"
      },
      {
        "label": "TLC: The Other Way season 8 premiere announcement",
        "url": "https://www.tlc.com/shows/90-day-fiance-the-other-way/articles/90-day-fiance-before-the-90-days-first-look-trailer-video-premiere-date-announcement"
      },
      {
        "label": "TV Insider: The Other Way season 8 cast announcement",
        "url": "https://www.tvinsider.com/swooon/gallery/90-day-fiance-the-other-way-season-8-cast-new-returning-couples/"
      }
    ]
  },
  {
    "personId": "p-nigel-andi-arc",
    "headline": "An Australian free spirit facing some decidedly specific relationship planning.",
    "bio": [
      "Nigel is introduced as Andi’s partner in Australia, with a laid-back, nomadic approach to life. Their relationship enters the announced Other Way cast as Andi prepares to leave a Nashville music career behind.",
      "TLC previews differing ideas about commitment, ethical non-monogamy and future family plans. Going with the flow sounds peaceful until someone needs the flow to provide a five-year plan. No season outcome is claimed yet."
    ],
    "originStory": "Nigel’s relationship with Andi and the planned move to Australia are his announced The Other Way Season 8 introduction. The opened cast sources do not establish their first meeting.",
    "journeys": [
      {
        "label": "Where it starts: Nigel & Andi",
        "partnerName": "Andi",
        "relationshipId": "ARC-D428916C0C37",
        "era": "The Other Way S8 · announced",
        "summary": "TLC previews Nigel and Andi navigating contrasting lifestyles and expectations for their relationship and family future. This chapter is announced, not an aired episode summary."
      }
    ],
    "sources": [
      {
        "label": "TLC: The Other Way season 8 announced couples",
        "url": "https://www.tlc.com/shows/90-day-fiance-the-other-way/photo-galleries/90-day-fiance-the-other-way-season-8-couples-bios"
      },
      {
        "label": "TLC: The Other Way season 8 premiere announcement",
        "url": "https://www.tlc.com/shows/90-day-fiance-the-other-way/articles/90-day-fiance-before-the-90-days-first-look-trailer-video-premiere-date-announcement"
      },
      {
        "label": "TV Insider: The Other Way season 8 cast announcement",
        "url": "https://www.tvinsider.com/swooon/gallery/90-day-fiance-the-other-way-season-8-cast-new-returning-couples/"
      }
    ]
  }
]

export const legacyTimelinesC: Record<string, CastProfileTimeline> = {
  "p-aladin-jallali": {
    "connections": [
      {
        "name": "Laura Jallali",
        "personId": "p-laura-jallali",
        "relationshipId": "ARC-8498E016A0E7",
        "context": "His original franchise partner, whose relocation to Qatar introduced their relationship."
      },
      {
        "name": "Maria Talebi",
        "context": "Reported later fiancée in 2020; no verified cast-page identity or current status."
      }
    ],
    "seasons": [
      {
        "show": "90 Day Fiancé: The Other Way",
        "season": 1,
        "title": "Qatar life and a Tunisian wedding",
        "recap": "Laura joined Aladin in Qatar before their wedding celebration in Tunisia. Differences over trust, expectations and family approval followed them to the tell-all, where the marriage was in serious trouble.",
        "status": "aired",
        "partnerPersonIds": [
          "p-laura-jallali"
        ],
        "relationshipId": "ARC-8498E016A0E7"
      }
    ]
  },
  "p-laura-jallali": {
    "connections": [
      {
        "name": "Aladin Jallali",
        "personId": "p-aladin-jallali",
        "relationshipId": "ARC-8498E016A0E7",
        "context": "The husband whose relationship with Laura introduced her to the franchise."
      },
      {
        "name": "Tony",
        "context": "Later online partner discussed in a 2020 interview, not a verified current partner or indexed cast identity."
      }
    ],
    "seasons": [
      {
        "show": "90 Day Fiancé: The Other Way",
        "season": 1,
        "title": "A new home and a complicated celebration",
        "recap": "Laura navigated life with Aladin in Qatar and a wedding celebration in Tunisia. Liam’s reluctance and the couple’s disagreements over their marriage made the family reunion anything but quiet.",
        "status": "aired",
        "partnerPersonIds": [
          "p-aladin-jallali"
        ],
        "relationshipId": "ARC-8498E016A0E7"
      }
    ]
  },
  "p-deavan-clegg": {
    "connections": [
      {
        "name": "Jihoon Lee",
        "personId": "p-jihoon-lee",
        "relationshipId": "ARC-6EC78319E282",
        "context": "Her original dating-app match, husband and father of their son."
      },
      {
        "name": "Christopher “Topher” Park",
        "context": "Later partner publicly introduced after her split from Jihoon; no verified catalog person ID."
      }
    ],
    "seasons": [
      {
        "show": "90 Day Fiancé: The Other Way",
        "season": 1,
        "title": "A family begins across two countries",
        "recap": "Deavan and Jihoon welcomed Taeyang and tried to move their relationship toward married family life in South Korea. Language barriers and uncertainty about the practical arrangements complicated the fresh start.",
        "status": "aired",
        "partnerPersonIds": [
          "p-jihoon-lee"
        ],
        "relationshipId": "ARC-6EC78319E282"
      },
      {
        "show": "90 Day Fiancé: The Other Way",
        "season": 2,
        "title": "Another move, familiar worries",
        "recap": "Deavan returned to South Korea with her children as money, housing and responsibility became central disagreements. Their wedding celebration did not resolve the strain, and the end-of-season update reported their split and her return to America.",
        "status": "aired",
        "partnerPersonIds": [
          "p-jihoon-lee"
        ],
        "relationshipId": "ARC-6EC78319E282"
      }
    ]
  },
  "p-jihoon-lee": {
    "connections": [
      {
        "name": "Deavan Clegg",
        "personId": "p-deavan-clegg",
        "relationshipId": "ARC-6EC78319E282",
        "context": "His original franchise partner, wife during the series and mother of their son."
      }
    ],
    "seasons": [
      {
        "show": "90 Day Fiancé: The Other Way",
        "season": 1,
        "title": "Online dating becomes family life",
        "recap": "Jihoon and Deavan faced the arrival of their son while preparing to unite their family in South Korea. Parenthood, marriage plans and communication difficulties made their first season far more than a first-date story.",
        "status": "aired",
        "partnerPersonIds": [
          "p-deavan-clegg"
        ],
        "relationshipId": "ARC-6EC78319E282"
      },
      {
        "show": "90 Day Fiancé: The Other Way",
        "season": 2,
        "title": "Building a home proves difficult",
        "recap": "Deavan’s return with her children put Jihoon’s financial readiness and parenting responsibilities under scrutiny. The couple celebrated their marriage, but the season’s closing update reported they had separated.",
        "status": "aired",
        "partnerPersonIds": [
          "p-deavan-clegg"
        ],
        "relationshipId": "ARC-6EC78319E282"
      }
    ]
  },
  "p-melyza-zeta": {
    "connections": [
      {
        "name": "Tim Clarkson",
        "personId": "p-tim-clarkson",
        "relationshipId": "ARC-0745DBD6E961",
        "context": "Her original franchise partner; this is Tim Clarkson, not Tim Malcolm."
      }
    ],
    "seasons": [
      {
        "show": "90 Day Fiancé: The Other Way",
        "season": 2,
        "title": "A move cannot fast-forward forgiveness",
        "recap": "Tim came to Colombia to rebuild the relationship after admitting he had been unfaithful. Melyza’s doubts and her family’s skepticism kept trust at the center of their reunion.",
        "status": "aired",
        "partnerPersonIds": [
          "p-tim-clarkson"
        ],
        "relationshipId": "ARC-0745DBD6E961"
      }
    ]
  },
  "p-tim-clarkson": {
    "connections": [
      {
        "name": "Melyza Zeta",
        "personId": "p-melyza-zeta",
        "relationshipId": "ARC-0745DBD6E961",
        "context": "His original franchise partner, whom he met during her time working as an au pair in America."
      }
    ],
    "seasons": [
      {
        "show": "90 Day Fiancé: The Other Way",
        "season": 2,
        "title": "Texas to Colombia, with trust to rebuild",
        "recap": "Tim moved to Colombia after acknowledging that he had cheated on Melyza. The reunion tested whether his commitment could withstand her understandable doubts and her family’s reservations.",
        "status": "aired",
        "partnerPersonIds": [
          "p-melyza-zeta"
        ],
        "relationshipId": "ARC-0745DBD6E961"
      }
    ]
  },
  "p-yazan-abo-horira": {
    "connections": [
      {
        "name": "Brittany Banks",
        "personId": "p-brittany-banks",
        "relationshipId": "ARC-97EBA9D0AEA9",
        "context": "His original franchise partner, introduced by his sister over video chat."
      },
      {
        "name": "Leena",
        "context": "Later fiancée confirmed in Yazan’s 2021 interview; no verified catalog person ID or present outcome."
      }
    ],
    "seasons": [
      {
        "show": "90 Day Fiancé: The Other Way",
        "season": 2,
        "title": "A Jordan reunion meets family expectations",
        "recap": "Brittany came to Jordan to be with Yazan, but their different expectations about marriage, religion and family acceptance drove recurring conflict. Her unresolved prior marriage added another obstacle to their plans.",
        "status": "aired",
        "partnerPersonIds": [
          "p-brittany-banks"
        ],
        "relationshipId": "ARC-97EBA9D0AEA9"
      }
    ]
  },
  "p-jonathan-ally-arc": {
    "connections": [
      {
        "name": "Ally",
        "personId": "p-ally-jonathan-arc",
        "relationshipId": "ARC-613995429020",
        "context": "Original partner in TLC’s announced season 8 storyline; upcoming as of October 1, 2026."
      }
    ],
    "seasons": [
      {
        "show": "90 Day Fiancé: The Other Way",
        "season": 8,
        "title": "An announced first chapter",
        "recap": "TLC previews Jonathan’s move to Singapore for pregnant partner Ally, with work and family conversations still unresolved. This is an announced season premise, not an account of aired events.",
        "status": "announced",
        "partnerPersonIds": [
          "p-ally-jonathan-arc"
        ],
        "relationshipId": "ARC-613995429020"
      }
    ]
  },
  "p-ally-jonathan-arc": {
    "connections": [
      {
        "name": "Jonathan",
        "personId": "p-jonathan-ally-arc",
        "relationshipId": "ARC-613995429020",
        "context": "Original partner in TLC’s announced season 8 storyline; upcoming as of October 1, 2026."
      }
    ],
    "seasons": [
      {
        "show": "90 Day Fiancé: The Other Way",
        "season": 8,
        "title": "An announced first chapter",
        "recap": "TLC previews Ally welcoming Jonathan to Singapore while preparing for parenthood and telling her family about the pregnancy. The season is announced, with no aired resolution yet.",
        "status": "announced",
        "partnerPersonIds": [
          "p-jonathan-ally-arc"
        ],
        "relationshipId": "ARC-613995429020"
      }
    ]
  },
  "p-zuri-ben-arc": {
    "connections": [
      {
        "name": "Ben",
        "personId": "p-ben-zuri-arc",
        "relationshipId": "ARC-2F1D8514A67A",
        "context": "Original partner in TLC’s announced season 8 storyline; upcoming as of October 1, 2026."
      }
    ],
    "seasons": [
      {
        "show": "90 Day Fiancé: The Other Way",
        "season": 8,
        "title": "An announced first chapter",
        "recap": "TLC previews Zuri giving life in France with Ben another chance after an earlier move fell apart. Friends’ doubts and questions about their time apart are announced tensions, not confirmed outcomes.",
        "status": "announced",
        "partnerPersonIds": [
          "p-ben-zuri-arc"
        ],
        "relationshipId": "ARC-2F1D8514A67A"
      }
    ]
  },
  "p-ben-zuri-arc": {
    "connections": [
      {
        "name": "Zuri",
        "personId": "p-zuri-ben-arc",
        "relationshipId": "ARC-2F1D8514A67A",
        "context": "Original partner in TLC’s announced season 8 storyline; upcoming as of October 1, 2026."
      }
    ],
    "seasons": [
      {
        "show": "90 Day Fiancé: The Other Way",
        "season": 8,
        "title": "An announced first chapter",
        "recap": "TLC previews Ben and Zuri trying again to build a shared home in France. The announced storyline includes questions about adaptation, friends’ support and their time apart; it has not yet aired.",
        "status": "announced",
        "partnerPersonIds": [
          "p-zuri-ben-arc"
        ],
        "relationshipId": "ARC-2F1D8514A67A"
      }
    ]
  },
  "p-jessy-vittorio-arc": {
    "connections": [
      {
        "name": "Vittorio",
        "personId": "p-vittorio-jessy-arc",
        "relationshipId": "ARC-554CAC8794EF",
        "context": "Original partner in TLC’s announced season 8 storyline; upcoming as of October 1, 2026."
      }
    ],
    "seasons": [
      {
        "show": "90 Day Fiancé: The Other Way",
        "season": 8,
        "title": "An announced first chapter",
        "recap": "TLC previews Jessy’s move to Italy and the couple’s questions about commitment, intimacy and family support. This is an announced relationship chapter, not a recap of aired episodes.",
        "status": "announced",
        "partnerPersonIds": [
          "p-vittorio-jessy-arc"
        ],
        "relationshipId": "ARC-554CAC8794EF"
      }
    ]
  },
  "p-vittorio-jessy-arc": {
    "connections": [
      {
        "name": "Jessy",
        "personId": "p-jessy-vittorio-arc",
        "relationshipId": "ARC-554CAC8794EF",
        "context": "Original partner in TLC’s announced season 8 storyline; upcoming as of October 1, 2026."
      }
    ],
    "seasons": [
      {
        "show": "90 Day Fiancé: The Other Way",
        "season": 8,
        "title": "An announced first chapter",
        "recap": "TLC previews Vittorio and Jessy testing whether their Italian future can overcome questions about intimacy and family approval. The chapter is announced and has not aired.",
        "status": "announced",
        "partnerPersonIds": [
          "p-jessy-vittorio-arc"
        ],
        "relationshipId": "ARC-554CAC8794EF"
      }
    ]
  },
  "p-michelle-felix-arc": {
    "connections": [
      {
        "name": "Felix",
        "personId": "p-felix-michelle-arc",
        "relationshipId": "ARC-464FCC3AA26C",
        "context": "Original partner in TLC’s announced season 8 storyline; upcoming as of October 1, 2026."
      }
    ],
    "seasons": [
      {
        "show": "90 Day Fiancé: The Other Way",
        "season": 8,
        "title": "An announced first chapter",
        "recap": "TLC previews Michelle moving to Ghana for Felix while questioning how much she can trust the relationship and family expectations. No aired outcome or allegation of wrongdoing is established here.",
        "status": "announced",
        "partnerPersonIds": [
          "p-felix-michelle-arc"
        ],
        "relationshipId": "ARC-464FCC3AA26C"
      }
    ]
  },
  "p-felix-michelle-arc": {
    "connections": [
      {
        "name": "Michelle",
        "personId": "p-michelle-felix-arc",
        "relationshipId": "ARC-464FCC3AA26C",
        "context": "Original partner in TLC’s announced season 8 storyline; upcoming as of October 1, 2026."
      }
    ],
    "seasons": [
      {
        "show": "90 Day Fiancé: The Other Way",
        "season": 8,
        "title": "An announced first chapter",
        "recap": "TLC previews Felix and Michelle trying to build a life in Ghana amid questions about trust and family intentions. These are announced tensions, not verified conclusions or aired outcomes.",
        "status": "announced",
        "partnerPersonIds": [
          "p-michelle-felix-arc"
        ],
        "relationshipId": "ARC-464FCC3AA26C"
      }
    ]
  },
  "p-andi-nigel-arc": {
    "connections": [
      {
        "name": "Nigel",
        "personId": "p-nigel-andi-arc",
        "relationshipId": "ARC-D428916C0C37",
        "context": "Original partner in TLC’s announced season 8 storyline; upcoming as of October 1, 2026."
      }
    ],
    "seasons": [
      {
        "show": "90 Day Fiancé: The Other Way",
        "season": 8,
        "title": "An announced first chapter",
        "recap": "TLC previews Andi’s Australian relocation and the pair’s differing expectations about lifestyle, non-monogamy and family plans. No aired resolution is available before the announced premiere.",
        "status": "announced",
        "partnerPersonIds": [
          "p-nigel-andi-arc"
        ],
        "relationshipId": "ARC-D428916C0C37"
      }
    ]
  },
  "p-nigel-andi-arc": {
    "connections": [
      {
        "name": "Andi",
        "personId": "p-andi-nigel-arc",
        "relationshipId": "ARC-D428916C0C37",
        "context": "Original partner in TLC’s announced season 8 storyline; upcoming as of October 1, 2026."
      }
    ],
    "seasons": [
      {
        "show": "90 Day Fiancé: The Other Way",
        "season": 8,
        "title": "An announced first chapter",
        "recap": "TLC previews Nigel and Andi navigating contrasting lifestyles and expectations for their relationship and family future. This chapter is announced, not an aired episode summary.",
        "status": "announced",
        "partnerPersonIds": [
          "p-andi-nigel-arc"
        ],
        "relationshipId": "ARC-D428916C0C37"
      }
    ]
  }
}

